import type {
	GuiComponentApiModel,
	ButtonApiModel as GuiComponentButton,
	ButtonHyperlinkApiModel as GuiComponentButtonHyperlink,
	ButtonIconApiModel as GuiComponentButtonIcon,
	ImageApiModel as GuiComponentImage,
	ListApiModel as GuiComponentList,
	ListDirectionalApiModel as GuiComponentListDirectional,
	ListedAtReceptionsApiModel as GuiComponentListedAtReceptions,
	RecentVisitsApiModel as GuiComponentRecentVisits,
	SearchApiModel as GuiComponentSearch,
	SpacerApiModel as GuiComponentSpacer,
	TextApiModel as GuiComponentText,
} from '@api/generated/models';
import { getAbsoluteRoute } from '@helpers/route';
import { Singleton } from '@visiba/instantiation';
import { EnvironmentService } from '../environment.service/mod';
import { FlexibleBaseSpecification, FlexibleModelBase } from './models/flexible_base';
import {
	type AccessFlow,
	FlexibleActionCardModel,
	FlexibleButtonHyperlinkModel,
	FlexibleButtonIconModel,
	FlexibleButtonModel,
	FlexibleImageModel,
	FlexibleListDirectionalModel,
	FlexibleListedAtReceptionsModel,
	FlexibleListModel,
	FlexibleRecentVisitsModel,
	FlexibleRockBottomModel,
	FlexibleSearchModel,
	FlexibleSpacerModel,
	FlexibleTextModel,
} from './models/mod';

export type ComponentUnion =
	| FlexibleRockBottomModel
	| FlexibleActionCardModel
	| FlexibleButtonModel
	| FlexibleButtonIconModel
	| FlexibleButtonHyperlinkModel
	| FlexibleImageModel
	| FlexibleListModel
	| FlexibleListDirectionalModel
	| FlexibleTextModel
	| FlexibleSearchModel
	| FlexibleSpacerModel
	| FlexibleListedAtReceptionsModel
	| FlexibleRecentVisitsModel;

@Singleton()
export class FlexibleComponentService {
	constructor(private readonly environmentService: EnvironmentService) {
		// Empty
	}

	public produce(type: 'Button', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleButtonModel;
	public produce(type: 'Button.Icon', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleButtonIconModel;
	public produce(type: 'Button.Hyperlink', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleButtonHyperlinkModel;
	public produce(type: 'Image', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleImageModel;
	public produce(type: 'List', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleListModel;
	public produce(type: 'List.Directional', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleListDirectionalModel;
	public produce(type: 'Text', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleTextModel;
	public produce(type: 'Search', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleSearchModel;
	public produce(type: 'Spacer', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleSpacerModel;
	public produce(
		type: 'ListedAtReceptions',
		apiComponent: GuiComponentApiModel,
		accessFlow?: AccessFlow,
	): FlexibleListedAtReceptionsModel;
	public produce(type: 'RecentVisits', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleRecentVisitsModel;
	/**
	 * NOTE: ActionCard is not a part of the backend spec. We're intentionally
	 * defining it on the frontend.
	 */
	public produce(type: 'ActionCard', apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleActionCardModel;
	public produce(type: string, apiComponent: GuiComponentApiModel, accessFlow?: AccessFlow): FlexibleModelBase; // Anything else than above should throw error for the developer
	public produce(
		type:
			| 'Button'
			| 'Button.Icon'
			| 'Button.Hyperlink'
			| 'Image'
			| 'List'
			| 'List.Directional'
			| 'Text'
			| 'Search'
			| 'Spacer'
			| 'ListedAtReceptions'
			| 'RecentVisits'
			| 'ActionCard'
			| string,
		apiComponent: GuiComponentApiModel,
		accessFlow?: AccessFlow,
	): ComponentUnion {
		const baseSpec = {
			id: apiComponent.Id,
			order: apiComponent.Order,
			horizontalAlignment: apiComponent.HorizontalAlignment,
		} as FlexibleBaseSpecification;

		function sortFunction(a: GuiComponentApiModel, b: GuiComponentApiModel): 1 | 0 | -1 {
			if (a.Order > b.Order) return 1;
			if (a.Order < b.Order) return -1;

			return 0;
		}

		switch (type) {
			case 'Button': {
				const message = apiComponent.Data as GuiComponentButton;
				let url = message.Url;
				if (message.OpenUrlWith === 'SameTab') {
					url = this.convertUrlToAbsoluteRoute(message.Url);
				}

				return new FlexibleButtonModel({
					...baseSpec,
					url,
					hasBrandedNavbar: message.HasBrandedNavbar,
					openAnotherApp: message.OpenAnotherApp,
					requiresAuthentication: message.RequiresAuthentication,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
					openUrlWith: message.OpenUrlWith,
				});
			}
			case 'Button.Icon': {
				const message = apiComponent.Data as GuiComponentButtonIcon;
				let url = message.Url;
				if (message.OpenUrlWith === 'SameTab') {
					url = this.convertUrlToAbsoluteRoute(message.Url);
				}

				const hasExternalIllustration = message.IconSvgUrl != null || message.IconPngUrl != null;

				return new FlexibleButtonIconModel({
					...baseSpec,
					url,
					hasBrandedNavbar: message.HasBrandedNavbar,
					openAnotherApp: message.OpenAnotherApp,
					requiresAuthentication: message.RequiresAuthentication,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
					// TODO PAT-426: Allow null?
					subtitle: message.Subtitle ?? '',
					icon: hasExternalIllustration
						? {
								type: message.IconSvgUrl ? 'svgUrl' : 'pngUrl',
								origin: message.IconSvgUrl ?? message.IconPngUrl,
							}
						: null,
					openUrlWith: message.OpenUrlWith,
					channel: apiComponent.Channel ?? null,
				});
			}
			case 'Button.Hyperlink': {
				const message = apiComponent.Data as GuiComponentButtonHyperlink;
				let url = message.Url;
				if (message.OpenUrlWith === 'SameTab') {
					url = this.convertUrlToAbsoluteRoute(message.Url);
				}

				return new FlexibleButtonHyperlinkModel({
					...baseSpec,
					url,
					hasBrandedNavbar: message.HasBrandedNavbar,
					openAnotherApp: message.OpenAnotherApp,
					requiresAuthentication: message.RequiresAuthentication,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
					openUrlWith: message.OpenUrlWith,
				});
			}

			case 'Image': {
				const message = apiComponent.Data as GuiComponentImage;

				return new FlexibleImageModel({
					...baseSpec,
					// TODO PAT-426: Allow null?
					altText: message.AltText ?? '',
					imageUrl: message.ImageUrl,
					imageHeight: message.ImageHeight,
					imageWidth: message.ImageWidth,
				});
			}

			case 'List': {
				const message = apiComponent.Data as GuiComponentList;

				return new FlexibleListModel({
					...baseSpec,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
					items: message.Items.sort(sortFunction).map((component) => this.produce(component.Type, component) as FlexibleModelBase),
				});
			}
			case 'List.Directional': {
				const message = apiComponent.Data as GuiComponentListDirectional;

				return new FlexibleListDirectionalModel({
					...baseSpec,
					direction: message.Direction,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
					items: message.Items.sort(sortFunction).map((component) => this.produce(component.Type, component) as FlexibleModelBase),
				});
			}

			case 'Text': {
				const message = apiComponent.Data as GuiComponentText;

				return new FlexibleTextModel({
					...baseSpec,
					// TODO PAT-426: Allow null?
					value: message.Value ?? '',
					size: message.Size,
					weight: message.Weight,
					// TODO PAT-426: Allow null?
					color: message.Color ?? '',
				});
			}

			case 'Search': {
				const message = apiComponent.Data as GuiComponentSearch;

				return new FlexibleSearchModel({
					...baseSpec,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
				});
			}

			case 'Spacer': {
				const message = apiComponent.Data as GuiComponentSpacer;

				return new FlexibleSpacerModel({
					...baseSpec,
					// TODO PAT-426: Allow null?
					vertical: message.Vertical ?? '',
					// TODO PAT-426: Allow null?
					horizontal: message.Horizontal ?? '',
				});
			}

			case 'ListedAtReceptions': {
				const message = apiComponent.Data as GuiComponentListedAtReceptions;

				return new FlexibleListedAtReceptionsModel({
					...baseSpec,
					title: message.Title ?? '',
				});
			}

			case 'RecentVisits': {
				const message = apiComponent.Data as GuiComponentRecentVisits;

				return new FlexibleRecentVisitsModel({
					...baseSpec,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
				});
			}

			case 'ActionCard': {
				const message = apiComponent.Data as GuiComponentButtonIcon;
				let url = message.Url;
				if (message.OpenUrlWith === 'SameTab') {
					url = this.convertUrlToAbsoluteRoute(message.Url);
				}

				const hasExternalIllustration = message.IconSvgUrl != null || message.IconPngUrl != null;

				return new FlexibleActionCardModel({
					...baseSpec,
					url,
					hasBrandedNavbar: message.HasBrandedNavbar,
					openAnotherApp: message.OpenAnotherApp,
					requiresAuthentication: message.RequiresAuthentication,
					// TODO PAT-426: Allow null?
					title: message.Title ?? '',
					// TODO PAT-426: Allow null?
					subtitle: message.Subtitle ?? '',
					icon: hasExternalIllustration
						? {
								type: message.IconSvgUrl ? 'svgUrl' : 'pngUrl',
								origin: message.IconSvgUrl ?? message.IconPngUrl,
							}
						: null,
					openUrlWith: message.OpenUrlWith,
					accessFlow,
					channel: apiComponent.Channel ?? null,
				});
			}

			default: {
				const subdivideTypes = type.split('.');
				if (subdivideTypes.length === 1) {
					return new FlexibleRockBottomModel({
						...baseSpec,
					});
				}

				subdivideTypes.pop();
				const glueTogether = subdivideTypes.join('.');

				// In casting we trust 🙏
				return this.produce(glueTogether, apiComponent) as ComponentUnion;
			}
		}
	}

	private convertUrlToAbsoluteRoute(urlLike: string): string {
		const url = new URL(urlLike, window.location.origin);
		const isAbsoluteUrl = /^(?:[a-z][a-z\d+\-.]*:|\/\/)/i.test(urlLike);
		const normalizeUrlPathName = url.pathname[0] === '/' ? url.pathname.substring(1) : url.pathname;

		if (this.hostnameIsWithinSameDomain(url.hostname)) {
			return getAbsoluteRoute(normalizeUrlPathName + url.search);
		}

		return isAbsoluteUrl ? urlLike : getAbsoluteRoute(normalizeUrlPathName + url.search);
	}

	private hostnameIsWithinSameDomain(hostname: string): boolean {
		if (this.environmentService.hosting.location.includes(hostname)) {
			return true;
		}

		return false;
	}
}
